import { IVFSResponse } from '../types';
import BaseAPI from '../BaseAPI';
import { GetInviteIdResponseData, TeamSettings, TeamTag } from './types';

/**
 * Teams API
 * Handles team-related operations
 */
class TeamsAPI extends BaseAPI {
  /**
   * Save the last selected team space
   * @param teamId - ID of the team
   */
  async saveLastSelectedTeamSpace(teamId: string): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.post(
        '/v1/company-shared-space/selected-team-space',
        {
          teamId,
        }
      ),
      errorMessage: 'Failed to save last selected team space',
    });
  }

  /**
   * Get join code for a team
   * @param teamId - ID of the team
   */
  async getJoinCode(
    teamId: string
  ): Promise<IVFSResponse<GetInviteIdResponseData>> {
    return this.wrapResponse<GetInviteIdResponseData>({
      promise: this.apiInstance.get(`/v2/team/${teamId}/member/join/code`),
      errorMessage: 'Failed to get join code',
    });
  }

  /**
   * Remove a member from a team
   * @param params - Parameters for removing a team member
   */
  async removeTeamMember({
    teamId,
    userId,
  }: {
    teamId: string;
    userId: string;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.delete(`/v2/team/${teamId}/member/${userId}`),
      errorMessage: 'Failed to remove team member',
    });
  }

  /**
   * Update team settings
   * @param params - Parameters for updating team settings
   */
  async updateTeamSettings({
    teamId,
    settings,
  }: {
    teamId: string;
    settings: TeamSettings;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.put(`/v2/team/${teamId}/settings`, settings),
      errorMessage: 'Failed to update team settings',
    });
  }

  /**
   * Schedule team deletion
   * @param teamId - ID of the team
   */
  async scheduleTeamDeletion(teamId: string): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.put(`/v2/team/${teamId}/deletion-schedule`),
      errorMessage: 'Failed to schedule team deletion',
    });
  }

  /**
   * Cancel scheduled team deletion
   * @param teamId - ID of the team
   */
  async unscheduleTeamDeletion(teamId: string): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.delete(`/v2/team/${teamId}/deletion-schedule`),
      errorMessage: 'Failed to cancel scheduled team deletion',
    });
  }

  /**
   * Add a webhook URL to a team
   * @param params - Parameters for adding a webhook URL
   */
  async addWebhookUrl({
    teamId,
    name,
    url,
  }: {
    teamId: string;
    name: string;
    url: string;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.post(
        `/v2/team/${teamId}/integrations/webhook`,
        {
          name,
          url,
        }
      ),
      errorMessage: 'Failed to add webhook URL',
    });
  }

  /**
   * Remove a webhook URL from a team
   * @param params - Parameters for removing a webhook URL
   */
  async removeWebhookUrl({
    teamId,
    url,
  }: {
    teamId: string;
    url: string;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.delete(
        `/v2/team/${teamId}/integrations/webhook`,
        {
          data: { url },
        }
      ),
      errorMessage: 'Failed to remove webhook URL',
    });
  }

  /**
   * Add a tag to a team
   * @param params - Parameters for adding a tag to a team
   */
  async addTeamTag({
    teamId,
    tag,
  }: {
    teamId: string;
    tag: TeamTag;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.post(`/v2/team/${teamId}/tag`, tag),
      errorMessage: 'Failed to add team tag',
    });
  }
}

export default TeamsAPI;
